import { useState, useCallback, useEffect } from 'react';
import { cva } from 'class-variance-authority';
import { Alert, Button, Flex } from '@nula/core-ui';
import { useDocDeletedStatus } from '@/lib/sync/hooks/use-doc-deleted-status';
import { useWorkspaceService } from '@/lib/workspace/use-workspace-service';
import { useYjsRegistry } from '@/lib/sync/hooks/yjs-registry-context';
import styles from './doc-deleted-banner.module.css';

const docDeletedBanner = cva(styles.base);

interface Props {
  entityId: string;
}

/**
 * DocDeletedBanner — shown when the active document has been remotely deleted.
 *
 * - Subscribes to the docDeletedBus via useDocDeletedStatus
 * - Offers a Restore action that re-inserts the doc in the workspace Y.Map
 * - Dismissible per session
 * - Full-width banner using core-ui Alert
 * - Accessible: role="alert" (provided by Alert), aria-live region, keyboard-dismissible
 */
export function DocDeletedBanner({ entityId }: Props) {
  const status = useDocDeletedStatus(entityId);
  const service = useWorkspaceService();
  const registry = useYjsRegistry();
  const [dismissed, setDismissed] = useState(false);
  const [restoring, setRestoring] = useState(false);

  // Reset dismissed state when entityId changes
  useEffect(() => {
    setDismissed(false);
  }, [entityId]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
  }, []);

  // Keyboard accessibility: Escape to dismiss
  useEffect(() => {
    const isVisible = status.state === 'deleted-remotely' && !dismissed;
    if (!isVisible) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleDismiss();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [status.state, dismissed, handleDismiss]);

  const handleRestore = useCallback(async () => {
    setRestoring(true);
    try {
      const entry = registry.getEntry(entityId);
      const meta = entry?.doc.getMap('metadata');
      const title = (meta?.get('title') as string) ?? 'Untitled';
      const kind =
        (meta?.get('documentType') as 'document' | 'canvas') ?? 'document';
      await service.restoreDoc(entityId, { title, kind });
      setDismissed(true);
    } finally {
      setRestoring(false);
    }
  }, [entityId, registry, service]);

  if (status.state !== 'deleted-remotely' || dismissed) return null;

  return (
    <Alert
      type="warning"
      banner
      showIcon
      message="This document was deleted by another user. Restore it to keep your changes."
      action={
        <Flex gap="sm" className={styles.actionRow}>
          <Button
            variant="primary"
            size="sm"
            loading={restoring}
            aria-label="Restore document"
            onClick={handleRestore}
          >
            Restore
          </Button>
          <Button
            variant="text"
            size="sm"
            aria-label="Dismiss deleted document warning"
            onClick={handleDismiss}
          >
            Dismiss
          </Button>
        </Flex>
      }
      className={docDeletedBanner()}
    />
  );
}
